import styled from "@emotion/styled";
import { DetectByEvent } from "../../types/types";
import Chart from "react-apexcharts";
import Spinner from "../../components/spinner";
import { useEffect, useState } from "react";
import theme from "../../theme";
import { useI18n } from "../../locales";
import { getEventTypeKey } from "../../utils/langUtils";

const { md } = theme.screen;

const DetectByPeriod: React.FC<{
  eventData?: DetectByEvent[];
  isGoogle?: boolean;
  eventDataIsLoading?: boolean;
  width?: string;
  height?: string;
  legend?: "bottom" | "left" | "right" | "top" | undefined;
}> = (props) => {
  const { t } = useI18n();
  const {
    eventData,
    isGoogle, // 사용리포트인지 여부
    eventDataIsLoading,
    width,
    height,
    legend,
  } = props;
  const [sortedData, setSortData] = useState<DetectByEvent[]>([]);

  useEffect(() => {
    if (!eventData) return;
    let reorder = eventData.filter(
      (el) =>
        el.event !== "전체농기계" &&
        el.event !== "운행한농기계" &&
        el.event !== "위험지역진입" &&
        el.event !== "도난감지"
    );

    // 차량전복 데이터 찾기 (다국어 지원)
    let index = reorder.findIndex((el) => {
      const eventKey = getEventTypeKey(el.event, t);
      return eventKey === "vehicleRolloverEvent" || el.event === "차량전복";
    });

    // 차량전복 데이터가 있으면 해당 데이터 인덱스를 0번으로 변경 (제일 앞으로 강조)
    if (index > -1) {
      let target = reorder[index];
      reorder.splice(index, 1);
      reorder.unshift(target);
    }
    return setSortData(reorder);
  }, [eventData, t]);

  return (
    <Wrapper isGoogle={isGoogle}>
      {eventDataIsLoading && <Spinner />}
      {!eventDataIsLoading && eventData && (
        <>
          <Header>{t.statistics.periodDetections}</Header>
          <Body>
            <Chart
              options={{
                chart: {
                  type: "donut",
                },
                labels: sortedData.map((el): string => {
                  const eventKey = getEventTypeKey(el.event, t);
                  // 언어팩 키가 있으면 언어팩 값 사용, 없으면 원본 값 사용
                  if (eventKey && eventKey !== "normal") {
                    const translatedValue = t.control[eventKey as keyof typeof t.control];
                    return typeof translatedValue === "string" ? translatedValue : el.event;
                  }
                  return el.event;
                }),
                legend: {
                  position: legend ? legend : "right",
                },
                colors: [
                  "#f8555a",
                  "#2E93fA",
                  "#66DA26",
                  "#FF9800",
                  "#8E78FF",
                ],
              }}
              type="donut"
              height={height ? height : 300}
              width={width ? width : 500}
              series={sortedData.map((el) => el.cnt)}
            />
          </Body>
        </>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div<{ isGoogle?: boolean }>`
  min-width: 534px;
  max-width: 534px;
  background: #f9f9f9;
  box-shadow: ${({ isGoogle }) =>
    !isGoogle && `0px 4px 10px rgba(0, 0, 0, 0.25)`};
  border: ${({ isGoogle }) => isGoogle && `3px solid #ececec`};
  border-radius: 15px;
  /* height: 100%; */
  padding: 20px;
  display: flex;
  flex-direction: column;
  height: 382px;

  .apexcharts-legend {
    @media (min-width: ${md + 1}px) {
      justify-content: center !important;
      top: 0 !important;
    }
    @media (max-width: ${md}px) {
      ${({ isGoogle }) =>
        !isGoogle &&
        `
        top: 0 !important;
        gap: 8px;
      `}
    }
  }

  @media (max-width: ${md}px) {
    ${({ isGoogle }) =>
      !isGoogle &&
      `
        border-radius: 5px;
    background: ${theme.color.white};
    width: 100%;
    min-width: 100%;
    max-width: 100%;
    box-shadow: none;
    height: auto;
    min-height: none;
    max-height: none;
    padding: 14px;
    
     .apexcharts-legend-text {
    @media (max-width: ${md}px) {
      font-size: ${theme.fontSize.FONT_3} !important;
    }
  }
  .apexcharts-legend-marker {
    @media (max-width: ${md}px) {
      width: 10px !important;
      height: 10px !important;
    }
  }
  .apexcharts-legend-series {
    @media (max-width: ${md}px) {
      height: 14px !important;
    }
  }
  .apexcharts-pie-label {
    @media (max-width: ${md}px) {
      font-size: ${theme.fontSize.FONT_3} !important;
      font-weight: ${theme.fontWeight.NORMAL};
    }
  }
    `}
  }
`;

const Header = styled.h1`
  font-weight: 700;
  font-size: 15px;
  line-height: 18px;
  color: #484848;
  margin-bottom: 20px;

  @media (max-width: ${md}px) {
    font-size: ${theme.fontSize.FONT_1};
    font-weight: ${theme.fontWeight.SEMI_BOLD};
    margin-bottom: 12px;
  }
`;

const Body = styled.div`
  background: #fefefe;
  border-radius: 10px;
  flex: 1;
`;

export default DetectByPeriod;
